export type AggregatePage = "home" | "search" | "favorites" | "sell" | "messages" | "account" | "help" | "legal" | "community" | "new" | "assistant" | "rentals" | "listing";
export type AggregateKind = "access" | "pageview" | "active";
export type AggregatePayload = { kind: AggregateKind; page: AggregatePage; activeSeconds?: number; listingId?: string };
export type AggregateDocument = { accessClaimed: boolean };
export type AggregateClock = { lastAt: number; active: boolean; elapsedMs: number };
export type AggregateVisit = { key: string; page: AggregatePage; listingId?: string; started: boolean; available: boolean; clock: AggregateClock };
const PAGES: readonly AggregatePage[] = ["home", "search", "favorites", "sell", "messages", "account", "help", "legal", "community", "new", "assistant", "listing"];

/** Only a public product reference, never a person/browser/session identifier. */
export function validAggregateListing(value: unknown): value is string { return typeof value === "string" && /^[a-f0-9]{32}$/.test(value); }

export function aggregatePayload(kind: AggregateKind, page: AggregatePage, activeSeconds?: number, listingId?: string): AggregatePayload | null {
  if (!["access", "pageview", "active"].includes(kind) || !PAGES.includes(page)) return null;
  if (listingId !== undefined && (!validAggregateListing(listingId) || page !== "listing" || kind === "active")) return null;
  if (kind === "active") {
    if (typeof activeSeconds !== "number" || !Number.isInteger(activeSeconds) || activeSeconds < 0 || activeSeconds > 15) return null;
    return { kind, page, activeSeconds };
  }
  if (activeSeconds !== undefined && activeSeconds !== 0) return null;
  return { kind, page, ...(listingId ? { listingId } : {}) };
}

export function createAggregateVisit(page: AggregatePage, listingId: string | undefined, now: number, visible: boolean, focused: boolean): AggregateVisit {
  return { key: `${page}:${listingId || ""}`, page, ...(listingId ? { listingId } : {}), started: false, available: true, clock: { lastAt: now, active: visible && focused, elapsedMs: 0 } };
}

/** Memory-only guards prevent StrictMode replays, not deduplication of people. */
export function claimAggregateView(documentState: AggregateDocument, visit: AggregateVisit, visible: boolean, focused: boolean): AggregatePayload | null {
  if (visit.started || !visit.available || !visible || !focused) return null;
  const payload = aggregatePayload(documentState.accessClaimed ? "pageview" : "access", visit.page, undefined, visit.listingId);
  if (!payload) return null;
  visit.started = true;
  documentState.accessClaimed = true;
  return payload;
}

export function advanceAggregateClock(clock: AggregateClock, now: number, visible: boolean, focused: boolean): AggregateClock {
  if (!Number.isFinite(now)) return { ...clock, active: false };
  const delta = Math.max(0, now - clock.lastAt);
  // Long scheduling gaps are discarded, never backfilled after offline/sleep.
  const counted = clock.active && delta <= 30000 ? delta : 0;
  return { lastAt: now, active: visible && focused, elapsedMs: Math.max(0, clock.elapsedMs) + counted };
}

/** A delta is consumed once even if its network request fails. */
export function drainAggregateClock(clock: AggregateClock): { seconds: number; clock: AggregateClock } {
  const elapsed = Number.isFinite(clock.elapsedMs) ? Math.max(0, clock.elapsedMs) : 0;
  return { seconds: Math.min(15, Math.floor(elapsed / 1000)), clock: { ...clock, elapsedMs: elapsed % 1000 } };
}
